<template>
  <IntercomLayout>
    <template #topbar>
      <FaraidTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="messages-view">
      <div class="page-header">
        <h1 class="page-title">{{ $t('messages.title') }}</h1>
        <p class="page-subtitle">{{ $t('messages.subtitle') }}</p>
      </div>

      <!-- Filters -->
      <div class="filters-bar">
        <select v-model="statusFilter" class="filter-select" @change="fetchMessages">
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="read">Read</option>
          <option value="failed">Failed</option>
        </select>
        <div class="date-filters">
          <IntercomDateInput
            v-model="dateFrom"
            label="From"
            @update:model-value="fetchMessages"
          />
          <span class="date-sep">to</span>
          <IntercomDateInput
            v-model="dateTo"
            label="To"
            @update:model-value="fetchMessages"
          />
        </div>
      </div>

      <!-- Messages Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="messages"
          :loading="isLoading"
          hoverable
          @row-click="handleRowClick"
        >
          <template #cell-status="{ value }">
            <span class="msg-status" :class="value">{{ value }}</span>
          </template>
          <template #cell-channel="{ value }">
            <span class="msg-channel" :class="value">{{ value }}</span>
          </template>
          <template #cell-created_at="{ value }">
            {{ formatDateTime(value) }}
          </template>
        </IntercomTable>
        <p v-if="!isLoading && messages.length === 0" class="empty-text">
          No messages found
        </p>
      </IntercomCard>

      <!-- Pagination -->
      <div v-if="messages.length > 0" class="pagination">
        <IntercomButton variant="ghost" size="sm" :disabled="offset === 0" @click="prevPage">
          Previous
        </IntercomButton>
        <span class="page-info">
          Showing {{ offset + 1 }}-{{ offset + messages.length }}
        </span>
        <IntercomButton variant="ghost" size="sm" :disabled="messages.length < pageSize" @click="nextPage">
          Next
        </IntercomButton>
      </div>

      <!-- Detail Slide-over -->
      <Transition name="slide-over">
        <div v-if="selectedMessage" class="slide-over-backdrop" @click.self="selectedMessage = null">
          <div class="slide-over-panel">
            <div class="slide-over-header">
              <h3>Message Details</h3>
              <button class="close-btn" @click="selectedMessage = null">&times;</button>
            </div>
            <div class="slide-over-body">
              <div class="detail-row">
                <span class="detail-label">Recipient</span>
                <span class="detail-value">{{ selectedMessage.recipient_name || selectedMessage.recipient_phone }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Phone</span>
                <span class="detail-value">{{ selectedMessage.recipient_phone }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Channel</span>
                <span class="detail-value">{{ selectedMessage.channel }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Direction</span>
                <span class="detail-value">{{ selectedMessage.direction }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Status</span>
                <span class="detail-value">
                  <span class="msg-status" :class="selectedMessage.status">{{ selectedMessage.status }}</span>
                </span>
              </div>
              <div class="detail-row" v-if="selectedMessage.template_name">
                <span class="detail-label">Template</span>
                <span class="detail-value">{{ selectedMessage.template_name }}</span>
              </div>
              <div class="detail-row" v-if="selectedMessage.content">
                <span class="detail-label">Content</span>
                <span class="detail-value content-text">{{ selectedMessage.content }}</span>
              </div>
              <div class="detail-row" v-if="selectedMessage.error_details">
                <span class="detail-label">Error</span>
                <span class="detail-value error-text">{{ selectedMessage.error_details }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Created</span>
                <span class="detail-value">{{ formatDateTime(selectedMessage.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useFaraid } from '../composables/useFaraid.js'
import { useNavigation } from '../composables/useNavigation.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomDateInput,
} from '@shared/components/intercom'
import FaraidTopbar from '../components/FaraidTopbar.vue'
import dayjs from 'dayjs'

const { t } = useI18n()
const router = useRouter()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { getMessageLogs } = useFaraid()
const { navSections } = useNavigation()

const messages = ref([])
const isLoading = ref(false)
const selectedMessage = ref(null)
const statusFilter = ref('')
const dateFrom = ref(dayjs().subtract(30, 'day').format('YYYY-MM-DD'))
const dateTo = ref(dayjs().format('YYYY-MM-DD'))
const offset = ref(0)
const pageSize = 25

const columns = [
  { key: 'recipient_name', title: 'Recipient', width: '150px' },
  { key: 'recipient_phone', title: 'Phone', width: '130px' },
  { key: 'channel', title: 'Channel', width: '90px' },
  { key: 'template_name', title: 'Template', width: '150px' },
  { key: 'status', title: 'Status', width: '90px' },
  { key: 'created_at', title: 'Date', width: '150px' },
]

const formatDateTime = (date) => date ? dayjs(date).format('DD MMM YYYY, HH:mm') : ''

const fetchMessages = async () => {
  isLoading.value = true
  try {
    const params = { limit: pageSize, offset: offset.value }
    if (statusFilter.value) params.status = statusFilter.value
    if (dateFrom.value) params.date_from = dateFrom.value
    if (dateTo.value) params.date_to = dateTo.value
    const response = await getMessageLogs(params)
    messages.value = response.data || response.items || []
  } catch (error) {
    console.error('Failed to fetch messages:', error)
  } finally {
    isLoading.value = false
  }
}

const handleRowClick = (row) => {
  selectedMessage.value = selectedMessage.value?.id === row.id ? null : row
}

const prevPage = () => {
  offset.value = Math.max(0, offset.value - pageSize)
  fetchMessages()
}

const nextPage = () => {
  offset.value += pageSize
  fetchMessages()
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusiness()
  fetchMessages()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.messages-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-6);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

/* Filters */
.filters-bar {
  display: flex;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
  flex-wrap: wrap;
}

.filter-select {
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  background: var(--color-white);
}

.date-filters {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.date-sep {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

/* Status badges */
.msg-status {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  padding: 2px 8px;
  border-radius: var(--radius-full);
  text-transform: capitalize;
}

.msg-status.pending { background: var(--color-gray-100); color: var(--color-gray-600); }
.msg-status.sent { background: #dbeafe; color: #1e40af; }
.msg-status.delivered { background: #dcfce7; color: #166534; }
.msg-status.read { background: #bbf7d0; color: #14532d; }
.msg-status.failed { background: #fecaca; color: #991b1b; }

.msg-channel {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  padding: 2px 8px;
  border-radius: var(--radius-full);
  text-transform: capitalize;
}

.msg-channel.whatsapp { background: #dcfce7; color: #166534; }
.msg-channel.sms { background: #dbeafe; color: #1e40af; }
.msg-channel.email { background: #fef9c3; color: #854d0e; }

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
  margin-top: var(--space-4);
}

.page-info {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}

/* Slide-over */
.slide-over-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 50;
  display: flex;
  justify-content: flex-end;
}

.slide-over-panel {
  width: 420px;
  max-width: 90vw;
  background: var(--color-white);
  box-shadow: var(--shadow-xl);
  display: flex;
  flex-direction: column;
  height: 100%;
}

.slide-over-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--color-gray-200);
}

.slide-over-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: var(--color-gray-400);
  cursor: pointer;
}

.slide-over-body {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-5);
}

.detail-row {
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--color-gray-100);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  display: block;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-500);
  text-transform: uppercase;
  margin-bottom: 2px;
}

.detail-value {
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
}

.content-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.error-text {
  color: var(--color-error);
}

/* Transitions */
.slide-over-enter-active,
.slide-over-leave-active {
  transition: all 0.3s ease;
}

.slide-over-enter-active .slide-over-panel,
.slide-over-leave-active .slide-over-panel {
  transition: transform 0.3s ease;
}

.slide-over-enter-from,
.slide-over-leave-to {
  opacity: 0;
}

.slide-over-enter-from .slide-over-panel,
.slide-over-leave-to .slide-over-panel {
  transform: translateX(100%);
}
</style>
